import {
  Accordion,
  Box,
  Flex,
  Image,
  Typography,
  useUiKitComponents,
} from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import { VipStarIcon } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

import type { MenuItemType } from "../HeaderV4";

type NavbarItemMobileProps = MenuItemType & {
  closeBurgerMenu: () => void;
  clickHeaderSub: (targetUrl: string) => void;
  clickHeaderMain: (targetUrl: string) => void;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  dataTestId?: string;
};

export const NavbarItemMobile: FC<NavbarItemMobileProps> = ({
  id,
  variant,
  translations,
  items,
  closeBurgerMenu,
  clickHeaderSub,
  clickHeaderMain,
  action,
  getUrl,
  onCallAction,
  dataTestId,
}) => {
  const { Link } = useUiKitComponents();
  const { getDirectusImage } = useDirectus();

  const nestedAction = action?.action?.[0];
  let href: string | undefined;

  if (isExternalLink(nestedAction?.item)) {
    if (isTemplateUrl(nestedAction?.item)) {
      href = undefined;
    } else {
      href = nestedAction.item.href ?? undefined;
    }
  }
  if (isInternalLink(nestedAction?.item)) {
    href = nestedAction.item.page ? getUrl(nestedAction.item.page) : undefined;
  }

  const translation = translations?.[0];

  const onClickHeaderMain = (): void => {
    if (href) {
      closeBurgerMenu();
      clickHeaderMain(href);
    }
    if (action) {
      onCallAction(action);
      closeBurgerMenu();
    }
  };

  return (
    <Box d={{ display: "hidden" }} width="100%" data-testid={dataTestId}>
      {variant === "default" && (
        <Accordion.Item
          p={{ px: 0, py: 14 }}
          variant="secondary"
          key={id}
          title={
            <Link href={href} onClick={onClickHeaderMain}>
              <Typography size="large" weight="semibold">
                {translation?.name}
              </Typography>
            </Link>
          }
        >
          {items?.map((item, index) => {
            const itemAction =
              item?.menu_action_sub_items_id?.action?.action?.[0];
            let itemHref: string | undefined;

            if (isExternalLink(itemAction?.item)) {
              if (isTemplateUrl(itemAction?.item)) {
                itemHref = undefined;
              } else {
                itemHref = itemAction.item.href ?? undefined;
              }
            }
            if (isInternalLink(itemAction?.item)) {
              itemHref = itemAction.item.page
                ? getUrl(itemAction.item.page)
                : undefined;
            }

            return (
              <Flex
                key={item?.menu_action_sub_items_id?.id}
                align="center"
                gap={16}
                p={{ px: 18, py: 12 }}
                data-testid={`navbar-item-${index}`}
              >
                {item?.menu_action_sub_items_id?.icon?.id && (
                  <Image
                    value={getDirectusImage(
                      item.menu_action_sub_items_id.icon.id,
                      {
                        format: "svg",
                        width: 24,
                      },
                    )}
                  />
                )}
                <Link
                  href={itemHref}
                  onClick={() => {
                    if (itemHref) {
                      closeBurgerMenu();
                      clickHeaderSub(itemHref);
                    }
                    if (action) {
                      onCallAction(action);
                      closeBurgerMenu();
                    }
                  }}
                >
                  <Typography size="large">
                    {item?.menu_action_sub_items_id?.translations?.[0]?.name}
                  </Typography>
                </Link>
              </Flex>
            );
          })}
        </Accordion.Item>
      )}
      {variant === "thin" && (
        <Accordion.Item
          p={{ px: 0, py: 14 }}
          variant="secondary"
          key={id}
          title={
            <Link href={href} onClick={onClickHeaderMain}>
              <Typography size="large" weight="normal">
                {translation?.name}
              </Typography>
            </Link>
          }
        >
          {items?.map((item, index) => {
            const itemAction =
              item?.menu_action_sub_items_id?.action?.action?.[0];
            let itemHref: string | undefined;

            if (isExternalLink(itemAction?.item)) {
              if (isTemplateUrl(itemAction?.item)) {
                itemHref = undefined;
              } else {
                itemHref = itemAction.item.href ?? undefined;
              }
            }
            if (isInternalLink(itemAction?.item)) {
              itemHref = itemAction.item.page
                ? getUrl(itemAction.item.page)
                : undefined;
            }

            return (
              <Flex
                key={item?.menu_action_sub_items_id?.id}
                align="center"
                gap={16}
                p={{ px: 18, py: 12 }}
                data-testid={`navbar-item-${index}`}
              >
                {item?.menu_action_sub_items_id?.icon?.id && (
                  <Image
                    value={getDirectusImage(
                      item.menu_action_sub_items_id.icon.id,
                      {
                        format: "svg",
                        width: 24,
                      },
                    )}
                  />
                )}
                <Link
                  href={itemHref}
                  onClick={() => {
                    if (itemHref) {
                      closeBurgerMenu();
                      clickHeaderSub(itemHref);
                    }
                    if (action) {
                      onCallAction(action);
                      closeBurgerMenu();
                    }
                  }}
                >
                  <Typography size="large">
                    {item?.menu_action_sub_items_id?.translations?.[0]?.name}
                  </Typography>
                </Link>
              </Flex>
            );
          })}
        </Accordion.Item>
      )}

      {variant === "underline" && (
        <Link href={href} onClick={onClickHeaderMain}>
          <Flex
            as="span"
            align="center"
            justify="center"
            bg="surface-accent2-default"
            border={{ radius: "4px" }}
            p={8}
            m={{ my: 8 }}
            height={40}
          >
            <Typography
              size="large"
              weight="semibold"
              color="text-primary-inverted"
            >
              {translation?.name}
            </Typography>
          </Flex>
        </Link>
      )}

      {variant === "emphasized" && (
        <Link href={href} onClick={onClickHeaderMain}>
          <Flex
            as="span"
            bg="surface-neutral-hovered"
            p={{ py: 8 }}
            m={{ my: 8 }}
            md={{ p: { py: 16 } }}
            align="center"
            justify="center"
            border={{ radius: "8px" }}
            className="transition-all duration-200 ease-linear"
            height={40}
          >
            <Box m={{ mx: 4 }} as="span">
              <VipStarIcon width="24" height="24" viewBox="0 0 28 27" />
            </Box>

            <Typography size="large" color="text-accent-default">
              {translation?.name}
            </Typography>

            <Box m={{ mx: 4 }} as="span">
              <VipStarIcon width="24" height="24" viewBox="0 0 28 27" />
            </Box>
          </Flex>
        </Link>
      )}

      {variant === "highlighted" && (
        <Link href={href} onClick={onClickHeaderMain}>
          <Flex
            as="span"
            horizontalAlign="center"
            m={{ my: 8 }}
            align="center"
            justify="center"
            p={8}
            border={{ radius: "4px" }}
            width="100%"
            height={40}
            bg="surface-accent1-default"
          >
            <Typography
              size="large"
              weight="semibold"
              className="text-[var(--colors-border-primary)]"
            >
              {translation?.name}
            </Typography>
          </Flex>
        </Link>
      )}
    </Box>
  );
};
